// src/pages/Home.jsx
import { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useAuth } from "../hooks/useAuth";
import { useReclamos } from "../hooks/useReclamos";
import { LA_PLATA_CENTER, DEFAULT_ZOOM, CATEGORIAS_CONFIG, ESTADOS_CONFIG } from "../constants";
import apiService from "../services/api.service";
import { Button } from "../components/common/Button";
import { Input } from "../components/common/Input";
import { Select } from "../components/common/Select";
import { Badge } from "../components/common/Badge";
import { Alert } from "../components/common/Alert";
import { Loading } from "../components/common/Loading";
// import { ImageUploader } from "../components/common/ImageUploader";

// Configurar íconos de Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const MyIcon = L.icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Componente para manejar eventos del mapa
function MapEvents({ onMapDoubleClick }) {
  useMapEvents({
    dblclick(e) {
      if (e.latlng && !isNaN(e.latlng.lat) && !isNaN(e.latlng.lng)) {
        onMapDoubleClick(e.latlng);
      }
    }
  });
  return null;
}

export default function Home() {
  const { firebaseUser } = useAuth();
  const { reclamos, loading, createReclamo, deleteReclamo, error: reclamosError } = useReclamos(true);
  const navigate = useNavigate();

  const [newReclamoPos, setNewReclamoPos] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    titulo: "",
    descripcion: "",
    categoria: CATEGORIAS_CONFIG[0].value
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [reclamosFeedbacks, setReclamosFeedbacks] = useState({});
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(true);

  useEffect(() => {
    if (reclamosError) {
      setErrorMessage(reclamosError);
    }
  }, [reclamosError]);

  // Cargar TODOS los feedbacks de una sola vez
  useEffect(() => {
    const loadAllFeedbacks = async () => {
      setLoadingFeedbacks(true);

      try {
        const feedbacksAgrupados = await apiService.getAllFeedbacksAgrupados();
        setReclamosFeedbacks(feedbacksAgrupados);
      } catch (err) {
        console.error("Error loading feedbacks:", err);
        setReclamosFeedbacks({});
      } finally {
        setLoadingFeedbacks(false);
      }
    };

    loadAllFeedbacks();
  }, [reclamos]);

  const handleMapDoubleClick = (latlng) => {
    setNewReclamoPos(latlng);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newReclamoPos) return;

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      /*
      if (selectedImages.length > 0) {
        selectedImages.forEach((img, index) => {
          console.log(`Imagen ${index}:`, {
            nombre: img.name,
            tipo: img.type,
            tamaño: img.size,
            esFile: img instanceof File,
            constructor: img.constructor.name
          });
        });
      }
      */

      const payload = {
        ...formData,
        latitud: newReclamoPos.lat,
        longitud: newReclamoPos.lng
      };

      await createReclamo(payload, selectedImages);

      setSuccessMessage("Reclamo creado exitosamente");
      setShowForm(false);
      setFormData({ titulo: "", descripcion: "", categoria: CATEGORIAS_CONFIG[0].value });
      setNewReclamoPos(null);
      setSelectedImages([]);

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error('❌ Error creando reclamo:', err);
      setErrorMessage(err.message || "No se pudo crear el reclamo");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este reclamo?")) return;

    try {
      await deleteReclamo(id);
      setSuccessMessage("Reclamo eliminado exitosamente");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setErrorMessage(err.message || "No se pudo eliminar el reclamo");
    }
  };

  // Verificar si un reclamo puede ser eliminado (menos de 24 horas y sin feedbacks)
  const canDelete = (reclamoId, fechaCreacion) => {
    const ahora = new Date();
    const fechaReclamo = new Date(fechaCreacion);
    const horasTranscurridas = (ahora - fechaReclamo) / (1000 * 60 * 60);
    const tieneFeedbacks = (reclamosFeedbacks[reclamoId] || []).length > 0;

    return horasTranscurridas < 24 && !tieneFeedbacks;
  };

  // Obtener mensaje de por qué no se puede eliminar
  const getCannotDeleteReason = (reclamoId, fechaCreacion) => {
    const ahora = new Date();
    const fechaReclamo = new Date(fechaCreacion);
    const horasTranscurridas = (ahora - fechaReclamo) / (1000 * 60 * 60);
    const tieneFeedbacks = (reclamosFeedbacks[reclamoId] || []).length > 0;

    if (tieneFeedbacks && horasTranscurridas >= 24) {
      return "tiene feedbacks y pasaron más de 24h";
    } else if (tieneFeedbacks) {
      return "tiene feedbacks";
    } else if (horasTranscurridas >= 24) {
      return "pasaron más de 24h";
    }
    return "";
  };

  const getEstadoVariant = (estado) => {
    const config = ESTADOS_CONFIG.find(e => e.value === estado);
    if (!config) return "default";

    if (estado === "resuelto") return "success";
    if (estado === "en_progreso") return "info";
    if (estado === "rechazado") return "danger";
    return "warning";
  };

  // Filtrar reclamos válidos
  const validReclamos = reclamos.filter(r => {
    if (!r.latitud || !r.longitud || isNaN(Number(r.latitud)) || isNaN(Number(r.longitud))) {
      return false;
    }

    return true;
  });

  const getIconByEstado = (estado, isMyReclamo) => {
    if (isMyReclamo) return MyIcon;

    const PendienteIcon = L.icon({
      iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-yellow.png",
      shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });

    const EnProgresoIcon = L.icon({
      iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
      shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });

    const ResueltoIcon = L.icon({
      iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
      shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });

    switch (estado) {
      case "pendiente":
        return PendienteIcon;
      case "en_progreso":
        return EnProgresoIcon;
      case "resuelto":
        return ResueltoIcon;
      default:
        return DefaultIcon;
    }
  };

  if (loading) {
    return <Loading text="Cargando mapa de reclamos..." />;
  }

  return (
    <div style={{
      position: "relative",
      height: "100%",
      width: "100%",
      margin: "-32px",
    }}>
      {/* Mensajes de éxito/error */}
      {successMessage && (
        <div style={{ position: "absolute", top: "20px", right: "20px", zIndex: 1000, maxWidth: "400px" }}>
          <Alert variant="success" onClose={() => setSuccessMessage("")}>
            {successMessage}
          </Alert>
        </div>
      )}

      {errorMessage && (
        <div style={{ position: "absolute", top: "20px", right: "20px", zIndex: 1000, maxWidth: "400px" }}>
          <Alert variant="error" onClose={() => setErrorMessage("")}>
            {errorMessage}
          </Alert>
        </div>
      )}

      {/* Instrucciones */}
      <div style={{
        position: "absolute",
        top: "20px",
        left: "20px",
        zIndex: 1000,
        backgroundColor: "white",
        padding: "16px 20px",
        borderRadius: "12px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
        maxWidth: "300px"
      }}>
        <h3 style={{ margin: "0 0 8px 0", fontSize: "16px", fontWeight: "600", color: "#1e293b" }}>
          💡 Instrucciones
        </h3>
        <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#64748b", lineHeight: "1.5" }}>
          Haz <strong>doble clic</strong> en el mapa para crear un nuevo reclamo.
        </p>

        <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "12px" }}>
          <div style={{ fontSize: "13px", fontWeight: "600", color: "#64748b", marginBottom: "8px" }}>
            Leyenda:
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "13px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{ width: "12px", height: "12px", backgroundColor: "#fbbf24", borderRadius: "50%" }}></div>
              <span style={{ color: "#64748b" }}>Pendiente</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{ width: "12px", height: "12px", backgroundColor: "#3b82f6", borderRadius: "50%" }}></div>
              <span style={{ color: "#64748b" }}>En Progreso</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{ width: "12px", height: "12px", backgroundColor: "#10b981", borderRadius: "50%" }}></div>
              <span style={{ color: "#64748b" }}>Resuelto</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{ width: "12px", height: "12px", backgroundColor: "#ef4444", borderRadius: "50%" }}></div>
              <span style={{ color: "#64748b" }}>Mis reclamos</span>
            </div>
          </div>
        </div>
      </div>

      <MapContainer
        center={LA_PLATA_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{
          height: "100vh",
          width: "calc(100vw - 280px)",
          position: "fixed",
          left: "280px",
          zIndex: 1
        }}
        doubleClickZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapEvents onMapDoubleClick={handleMapDoubleClick} />

        {/* Marcadores de reclamos existentes */}
        {validReclamos.map(r => {
          const lat = Number(r.latitud);
          const lng = Number(r.longitud);
          const isMyReclamo = firebaseUser && r.usuario_uid === firebaseUser.uid;
          const markerIcon = getIconByEstado(r.estado, isMyReclamo);
          const categoria = CATEGORIAS_CONFIG.find(c => c.value === r.categoria);

          return (
            <Marker key={r.id} position={[lat, lng]} icon={markerIcon}>
              <Popup>
                {r.imagenes && r.imagenes.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      overflowX: 'auto',
                      paddingBottom: '8px'
                    }}>
                      {r.imagenes.slice(0, 3).map((imagen, idx) => (
                        <img
                          key={imagen.id || idx}
                          src={imagen.url}
                          alt={`Imagen ${idx + 1} del reclamo`}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(imagen.url, '_blank')}
                        />
                      ))}
                      {r.imagenes.length > 3 && (
                        <div style={{
                          width: '60px',
                          height: '60px',
                          backgroundColor: '#f1f5f9',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          color: '#64748b',
                          fontWeight: '600'
                        }}>
                          +{r.imagenes.length - 3}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div style={{ minWidth: "200px" }}>
                  <h4 style={{ margin: "0 0 8px 0", fontSize: "16px", fontWeight: "600", color: "#1e293b" }}>
                    {r.titulo}
                  </h4>
                  <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#64748b" }}>
                    {r.descripcion}
                  </p>

                  <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
                    <Badge variant="default">
                      {categoria?.icon} {categoria?.label || r.categoria}
                    </Badge>
                    <Badge variant={getEstadoVariant(r.estado)}>
                      {ESTADOS_CONFIG.find(e => e.value === r.estado)?.label || r.estado}
                    </Badge>
                    {r.promedio_calificacion > 0 && (
                      <Badge variant="warning">
                        ★ {r.promedio_calificacion.toFixed(1)}
                      </Badge>
                    )}
                  </div>

                  <button
                    onClick={() => navigate(`/reclamo/${r.id}`)}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      backgroundColor: "#00a680",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                      marginBottom: isMyReclamo ? "8px" : "0",
                      transition: "all 0.2s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#059669";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#00a680";
                    }}
                  >
                    Ver Detalle →
                  </button>

                  {isMyReclamo && (
                    <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e2e8f0" }}>
                      {loadingFeedbacks ? (
                        <div style={{
                          padding: "10px 12px",
                          backgroundColor: "#f1f5f9",
                          color: "#64748b",
                          border: "2px solid #e2e8f0",
                          borderRadius: "8px",
                          fontSize: "12px",
                          fontWeight: "600",
                          textAlign: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          lineHeight: "1.3"
                        }}>
                          <span>⏳</span>
                          <span>Verificando...</span>
                        </div>
                      ) : canDelete(r.id, r.fecha_creacion) ? (
                        <button
                          onClick={() => handleDelete(r.id)}
                          style={{
                            width: "100%",
                            padding: "10px 16px",
                            backgroundColor: "#fee2e2",
                            color: "#991b1b",
                            border: "2px solid #fecaca",
                            borderRadius: "8px",
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px"
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#ef4444";
                            e.target.style.color = "white";
                            e.target.style.borderColor = "#ef4444";
                            e.target.style.transform = "translateY(-1px)";
                            e.target.style.boxShadow = "0 4px 12px rgba(239, 68, 68, 0.3)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "#fee2e2";
                            e.target.style.color = "#991b1b";
                            e.target.style.borderColor = "#fecaca";
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = "none";
                          }}
                        >
                          <span>🗑️</span>
                          <span>Eliminar Reclamo</span>
                        </button>
                      ) : (
                        <div style={{
                          padding: "10px 12px",
                          backgroundColor: "#f1f5f9",
                          color: "#64748b",
                          border: "2px solid #e2e8f0",
                          borderRadius: "8px",
                          fontSize: "12px",
                          fontWeight: "600",
                          textAlign: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          lineHeight: "1.3"
                        }}>
                          <span>🔒</span>
                          <span>No se puede eliminar ({getCannotDeleteReason(r.id, r.fecha_creacion)})</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Marcador del nuevo reclamo */}
        {newReclamoPos && (
          <Marker position={[newReclamoPos.lat, newReclamoPos.lng]} icon={MyIcon}>
            <Popup>
              <div style={{ fontSize: "14px", fontWeight: "600", color: "#00a680" }}>
                📍 Nuevo reclamo aquí
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Modal de formulario */}
      {showForm && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          padding: "20px"
        }}>
          <form
            onSubmit={handleSubmit}
            style={{
              backgroundColor: "white",
              padding: "32px",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "500px",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)"
            }}
          >
            <h3 style={{
              margin: "0 0 24px 0",
              fontSize: "24px",
              fontWeight: "700",
              color: "#1e293b"
            }}>
              📝 Nuevo Reclamo
            </h3>

            <Input
              type="text"
              label="Título"
              placeholder="Ej: Poste de luz sin funcionar"
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              required
              disabled={isSubmitting}
            />

            <div style={{ marginBottom: "20px" }}>
              <label style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "#334155",
                marginBottom: "8px"
              }}>
                Descripción
              </label>
              <textarea
                placeholder="Describe el problema con detalle..."
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                required
                disabled={isSubmitting}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "16px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "8px",
                  outline: "none",
                  transition: "all 0.2s ease",
                  minHeight: "100px",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  resize: "vertical"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#00a680";
                  e.target.style.boxShadow = "0 0 0 3px rgba(0, 166, 128, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            <Select
              label="Categoría"
              value={formData.categoria}
              onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
              options={CATEGORIAS_CONFIG.map(c => ({
                value: c.value,
                label: `${c.icon} ${c.label}`
              }))}
              required
              disabled={isSubmitting}
            />

            {/* 
            <ImageUploader 
              onImagesChange={setSelectedImages}
              maxImages={5}
            /> 
            */}
            <div style={{
              padding: "12px 16px",
              backgroundColor: "#f1f5f9",
              borderRadius: "8px",
              marginBottom: "24px",
              fontSize: "14px",
              color: "#475569"
            }}>
              <strong>📍 Ubicación:</strong> {newReclamoPos?.lat.toFixed(4)}, {newReclamoPos?.lng.toFixed(4)}
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creando..." : "Crear Reclamo"}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="lg"
                onClick={() => {
                  setShowForm(false);
                  setNewReclamoPos(null);
                  setFormData({ titulo: "", descripcion: "", categoria: CATEGORIAS_CONFIG[0].value });
                  setSelectedImages([]);
                }}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}